import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useFinanceCategories,
  useCreateFinanceCategory,
  useUpdateFinanceCategory,
  useDeleteFinanceCategory,
} from '@/hooks/useFinance';
import type { FinanceCategory, FinanceKind } from '@/types/database';

const SWATCHES = [
  '#10b981', '#0ea5e9', '#8b5cf6', '#ef4444', '#f59e0b',
  '#6366f1', '#ec4899', '#14b8a6', '#f97316', '#64748b',
];

export function FinanceCategoriesTab() {
  const { categories, isLoading } = useFinanceCategories();
  const deleteCat = useDeleteFinanceCategory();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceCategory | null>(null);
  const [newKind, setNewKind] = useState<FinanceKind>('income');
  const [toDelete, setToDelete] = useState<FinanceCategory | null>(null);

  const grouped = useMemo(
    () => ({
      income: categories.filter((c) => c.kind === 'income'),
      expense: categories.filter((c) => c.kind === 'expense'),
    }),
    [categories],
  );

  const openNew = (kind: FinanceKind) => {
    setEditing(null);
    setNewKind(kind);
    setFormOpen(true);
  };

  const openEdit = (cat: FinanceCategory) => {
    setEditing(cat);
    setFormOpen(true);
  };

  const renderGroup = (title: string, list: FinanceCategory[], kind: FinanceKind) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => openNew(kind)}>
          <Plus className="mr-1 h-4 w-4" />
          Nova
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="px-1 py-2 text-xs text-muted-foreground">Nenhuma categoria.</p>
      ) : (
        <div className="space-y-1.5">
          {list.map((cat) => (
            <Card key={cat.id}>
              <CardContent className="flex items-center gap-3 p-3">
                <span
                  className="h-4 w-4 shrink-0 rounded-full"
                  style={{ backgroundColor: cat.color ?? '#64748b' }}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{cat.name}</span>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(cat)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-600"
                  onClick={() => setToDelete(cat)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  if (isLoading && categories.length === 0) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {renderGroup('Entradas', grouped.income, 'income')}
      {renderGroup('Saídas', grouped.expense, 'expense')}

      <CategoryFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        defaultKind={newKind}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Lançamentos que usam “{toDelete?.name}” ficarão sem categoria (não são excluídos).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (toDelete) deleteCat.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CategoryFormDialog({
  open,
  onOpenChange,
  editing,
  defaultKind,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: FinanceCategory | null;
  defaultKind: FinanceKind;
}) {
  const createCat = useCreateFinanceCategory();
  const updateCat = useUpdateFinanceCategory();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<FinanceKind>(defaultKind);
  const [color, setColor] = useState(SWATCHES[0]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setKind(editing.kind);
      setColor(editing.color ?? SWATCHES[0]);
    } else {
      setName('');
      setKind(defaultKind);
      setColor(defaultKind === 'expense' ? '#ef4444' : '#10b981');
    }
  }, [open, editing, defaultKind]);

  const submitting = createCat.isPending || updateCat.isPending;

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.warning('Informe o nome da categoria.');
      return;
    }
    try {
      if (editing) {
        await updateCat.mutateAsync({ id: editing.id, updates: { name: name.trim(), kind, color } });
        toast.success('Categoria atualizada.');
      } else {
        await createCat.mutateAsync({ name: name.trim(), kind, color });
        toast.success('Categoria criada.');
      }
      onOpenChange(false);
    } catch {
      // handled in mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{editing ? 'Editar categoria' : 'Nova categoria'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setKind('income')}
              className={cn(
                'rounded-lg border py-2 text-sm font-medium',
                kind === 'income' ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30' : 'text-muted-foreground',
              )}
            >
              Entrada
            </button>
            <button
              type="button"
              onClick={() => setKind('expense')}
              className={cn(
                'rounded-lg border py-2 text-sm font-medium',
                kind === 'expense' ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-950/30' : 'text-muted-foreground',
              )}
            >
              Saída
            </button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cat-name">Nome</Label>
            <Input id="cat-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Cor</Label>
            <div className="flex flex-wrap gap-2">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  aria-label={c}
                  className={cn(
                    'h-7 w-7 rounded-full border-2 transition-transform',
                    color === c ? 'scale-110 border-foreground' : 'border-transparent',
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button3D color="gray" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button3D>
          <Button3D onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
